/* eslint-disable import/no-cycle */
import {
  noSideCard,
  sideCardSize,
  tableWithOutSideCard,
  tableWithSideCard,
  withSideCard
} from '@hconnect/common/consts'
import {ArrowKey, scrollToEvent, useArrowKeys} from '@hconnect/common/hooks/useArrowKeys'
import {useWidth} from '@hconnect/common/hooks/useWidth'
import {PaginationOptions} from '@hconnect/uikit'
import {CardBox} from '@hconnect/uikit/src/lib2'
import {Button} from '@hm/fundamental'
import {mdiPlus} from '@mdi/js'
import {CircularProgress, Grid, Stack, Theme} from '@mui/material'
import React, {useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {BulkUploadPlantActionsButton, TasksTable, TasksTableFilters} from '../components'
import {FEATURE_NAMESPACE} from '../consts'
import {UseTasksTableFiltersReturn} from '../hooks'
import {SideCardItemMode, ViewMode, HProduceTask, UseTasksStatusesCountsParams} from '../types'
import {isOnlyPomSourceSelected, isPomSourceSelected} from '../utils'

import {BulkUploadPlantActionsDialog} from './BulkUploadPlantActionsDialog'
import {SideCard} from './SideCard'
import {TasksStatusesSummaryContainer} from './TasksStatusesSummaryContainer'

const getProgressSpinner = (showBottomProgress?: boolean) =>
  showBottomProgress && (
    <CircularProgress
      sx={{marginX: 'auto', marginTop: 2}}
      data-test-id="events-view-bottom-progress"
    />
  )

type Props = {
  events: HProduceTask[]
  isLoading: boolean
  statusesSummaryParams: UseTasksStatusesCountsParams
  viewMode: ViewMode
  setViewMode: (mode: ViewMode) => void
  tableFiltersOptions: UseTasksTableFiltersReturn
  paginationOptions?: PaginationOptions
  timezone: string
  showBottomProgress?: boolean
  areFiltersApplied: boolean
}

// eslint-disable-next-line complexity
export const TasksView: React.FC<Props> = (props) => {
  const {
    events,
    isLoading,
    setViewMode,
    paginationOptions,
    viewMode,
    tableFiltersOptions,
    areFiltersApplied
  } = props
  const {showDescriptions, sortOptions, sourceFilters} = tableFiltersOptions
  const isOnlyPomSelected = isOnlyPomSourceSelected(sourceFilters)
  const isPomSelected = isPomSourceSelected(sourceFilters)

  const [selectedTask, setSelectedTask] = useState<HProduceTask | undefined>(
    viewMode.mode === SideCardItemMode.Details
      ? events.find(({id}) => id === viewMode.itemId)
      : undefined
  )
  // This state is temporary solution because if task is not part of the table it wont load properly
  // It will be removed when load task from server will be implemented
  const [createdTask, setCreatedTask] = useState<HProduceTask | undefined>()
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false)

  const breakPoint = useWidth()

  const isSideCardShown = viewMode.mode !== SideCardItemMode.TableOnly
  const isSmall = breakPoint === 'xs' || breakPoint === 'sm'
  const isTableShown = !isSideCardShown || !isSmall
  const isStatusesSummaryShown = !isSmall
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const onRowClicked = useCallback(
    (clickedItem: HProduceTask) => {
      setCreatedTask(undefined)
      setViewMode({
        mode: SideCardItemMode.Details,
        itemId: clickedItem.id,
        externalId: clickedItem.externalId,
        taskPlantId: clickedItem.plantId,
        source: clickedItem.source
      })
      setSelectedTask(clickedItem)
    },
    [setViewMode]
  )

  const selectedItem =
    selectedTask ||
    (viewMode.mode === SideCardItemMode.Details ||
    viewMode.mode === SideCardItemMode.EditSingle ||
    viewMode.mode === SideCardItemMode.EditSeries
      ? events.find(({id}) => id === viewMode.itemId)
      : undefined)

  const arrowKeyHandler = useCallback(
    (arrow: ArrowKey) => {
      if (!events || !events.length) {
        return
      }

      const currentPosition = !selectedItem?.id
        ? -1
        : events.findIndex((e) => e.id === selectedItem?.id)
      let newPosition = -1
      if (arrow === 'up') {
        if (currentPosition === -1) {
          newPosition = events.length - 1
        } else {
          newPosition = (currentPosition - 1 + events.length) % events.length
        }
      }
      if (arrow === 'down') {
        if (currentPosition === -1) {
          newPosition = 0
        } else {
          newPosition = (currentPosition + 1) % events.length
        }
      }
      scrollToEvent(arrow, newPosition)

      const newEventToBeSelected = events[newPosition]
      if (newEventToBeSelected) {
        onRowClicked(newEventToBeSelected)
      }
    },
    [events, selectedItem?.id, onRowClicked]
  )

  useArrowKeys(arrowKeyHandler)

  const tableSize = (isSideCardShown ? withSideCard : noSideCard).get(breakPoint) ?? 'S'

  // Determine next view mode on close
  // existingTask - task before edit, initial value or undefined if creating new task
  // savedTask - task after edit or undefined if task was deleted
  const determineNextViewMode = (
    existingTask?: HProduceTask,
    savedTask?: HProduceTask
  ): ViewMode => {
    const isNewTask = !existingTask && savedTask
    if (existingTask) {
      // 'editMode'
      return {mode: SideCardItemMode.Details, itemId: existingTask.id}
    } else if (isNewTask) {
      // 'createMode'
      return {mode: SideCardItemMode.TableOnly}
    } else {
      // 'detailsMode'
      return {mode: SideCardItemMode.TableOnly}
    }
  }

  const doClose = (original?: HProduceTask, editVersion?: HProduceTask) => {
    if (viewMode.mode === SideCardItemMode.Create && !original && editVersion) {
      setCreatedTask(editVersion)
      setViewMode({mode: SideCardItemMode.Details, itemId: editVersion.id})
      return
    }
    const nextMode: ViewMode = determineNextViewMode(original, editVersion)
    setViewMode(nextMode)
  }

  const handleAddTaskClick = () => {
    setViewMode({mode: SideCardItemMode.Create})
  }

  return (
    <Stack flexGrow={1} spacing={2} useFlexGap>
      {isStatusesSummaryShown && (
        <TasksStatusesSummaryContainer params={props.statusesSummaryParams} />
      )}
      <Grid container flexGrow={1} overflow={'hidden'} spacing={2}>
        {isTableShown && (
          <Grid
            item
            height={'100%'}
            {...(isSideCardShown ? tableWithSideCard : tableWithOutSideCard)}
          >
            <CardBox
              sx={(theme: Theme) => ({
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                [theme.breakpoints.between('md', 'xl')]: {
                  zoom: 0.8
                }
              })}
            >
              <TasksTableFilters
                isSmall={isSmall}
                onAddTaskClick={handleAddTaskClick}
                onBulkUploadClick={() => setIsBulkUploadOpen(true)}
                {...tableFiltersOptions}
              />
              {isSmall && !isOnlyPomSelected && (
                <Button
                  type="button"
                  variant="primary"
                  size="large"
                  icon={mdiPlus}
                  iconPosition="left"
                  onClick={handleAddTaskClick}
                  className="mb-16px w-full"
                  data-test-id="create_task_button"
                >
                  {t('tasksTable.action.createNew')}
                </Button>
              )}
              {isSmall && isPomSelected && (
                <BulkUploadPlantActionsButton
                  onClick={() => setIsBulkUploadOpen(true)}
                  className="mb-16px w-full"
                />
              )}

              <TasksTable
                data={events}
                onRowClick={onRowClicked}
                paginationOptions={paginationOptions}
                sortOptions={sortOptions}
                size={tableSize}
                selectedItemId={selectedItem?.id}
                isLoading={isLoading}
                showDescriptions={showDescriptions}
                timezone={props.timezone}
                areFiltersApplied={areFiltersApplied}
              />
              {getProgressSpinner(props.showBottomProgress)}
            </CardBox>
          </Grid>
        )}
        {isSideCardShown && (
          <Grid
            item
            {...sideCardSize}
            sx={{
              height: '100%',
              overflow: 'hidden'
            }}
          >
            <SideCard
              {...viewMode}
              task={createdTask ?? selectedItem}
              setMode={setViewMode}
              doClose={doClose}
              timezone={props.timezone}
            />
          </Grid>
        )}
      </Grid>
      {/* Mounted only while open: the closed fundamental Modal stays in the DOM with its file input */}
      {isBulkUploadOpen && (
        <BulkUploadPlantActionsDialog open onClose={() => setIsBulkUploadOpen(false)} />
      )}
    </Stack>
  )
}
